import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { setLoading } from './LoadingSlice';
import BaseUrl from '../../BaseUrl';

// API Base URL
const API_BASE_URL = BaseUrl.bloodbankurl
const AUTH_API_BASE_URL = `${BaseUrl.authurl}Auth`;

// --- Helper Enums and Functions ---
export const BloodType = {
  A_Negative: 0,
  A_Positive: 1,
  B_Negative: 2,
  B_Positive: 3,
  AB_Negative: 4,
  AB_Positive: 5,
  O_Negative: 6,
  O_Positive: 7
};

export const getBloodTypeString = (bloodType) => {
  switch (bloodType) {
    case BloodType.A_Negative: return 'A-';
    case BloodType.A_Positive: return 'A+';
    case BloodType.B_Negative: return 'B-';
    case BloodType.B_Positive: return 'B+';
    case BloodType.AB_Negative: return 'AB-';
    case BloodType.AB_Positive: return 'AB+';
    case BloodType.O_Negative: return 'O-';
    case BloodType.O_Positive: return 'O+';
    default: return 'Unknown';
  }
};

export const getBloodTypeNumber = (bloodTypeString) => {
  switch (bloodTypeString) {
    case 'A-': return BloodType.A_Negative;
    case 'A+': return BloodType.A_Positive;
    case 'B-': return BloodType.B_Negative;
    case 'B+': return BloodType.B_Positive;
    case 'AB-': return BloodType.AB_Negative;
    case 'AB+': return BloodType.AB_Positive;
    case 'O-': return BloodType.O_Negative;
    case 'O+': return BloodType.O_Positive;
    default: return BloodType.O_Positive;
  }
};

// --- Async Thunks ---

export const fetchPendingBloodBankRequestForms = createAsyncThunk(
  'bloodBank/fetchPendingBloodBankRequestForms',
  async (_, { dispatch, rejectWithValue }) => {
    dispatch(setLoading(true));
    try {
      const response = await axios.get(
        `${API_BASE_URL}BloodBankRequestForms/PendingForms`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    } finally {
      dispatch(setLoading(false));
    }
  }
);

export const fetchAvailableBloodUnits = createAsyncThunk(
  'bloodBank/fetchAvailableBloodUnits',
  async (_, { dispatch, rejectWithValue }) => {
    dispatch(setLoading(true));
    try {
      const response = await axios.get(`${API_BASE_URL}AvailableBloodUnits`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    } finally {
      dispatch(setLoading(false));
    }
  }
);

export const addAvailableBloodUnit = createAsyncThunk(
  'bloodBank/addAvailableBloodUnit',
  async (bloodUnit, { dispatch, rejectWithValue }) => {
    dispatch(setLoading(true));
    try {
      const response = await axios.post(`${API_BASE_URL}AvailableBloodUnits`, bloodUnit);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    } finally {
      dispatch(setLoading(false));
    }
  }
);

export const fetchBloodBankInventories = createAsyncThunk(
  'bloodBank/fetchBloodBankInventories',
  async (_, { dispatch, rejectWithValue }) => {
    dispatch(setLoading(true));
    try {
      const response = await axios.get(`${API_BASE_URL}BloodBankInventories`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    } finally {
      dispatch(setLoading(false));
    }
  }
);

export const addBloodBankInventory = createAsyncThunk(
  'bloodBank/addBloodBankInventory',
  async (inventory, { dispatch, rejectWithValue }) => {
    dispatch(setLoading(true));
    try {
      const response = await axios.post(`${API_BASE_URL}BloodBankInventories`, inventory);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    } finally {
      dispatch(setLoading(false));
    }
  }
);

export const fetchBloodBankRequestForms = createAsyncThunk(
  'bloodBank/fetchBloodBankRequestForms',
  async (_, { dispatch, rejectWithValue }) => {
    dispatch(setLoading(true));
    try {
      const response = await axios.get(`${API_BASE_URL}BloodBankRequestForms`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    } finally {
      dispatch(setLoading(false));
    }
  }
);

export const addBloodBankRequestForm = createAsyncThunk(
  'bloodBank/addBloodBankRequestForm',
  async (requestForm, { dispatch, rejectWithValue }) => {
    dispatch(setLoading(true));
    try {
      const response = await axios.post(`${API_BASE_URL}BloodBankRequestForms`, requestForm);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    } finally {
      dispatch(setLoading(false));
    }
  }
);

export const fetchPatientsBySearch = createAsyncThunk(
  'bloodBank/fetchPatientsBySearch',
  async (searchTerm, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.get(`${AUTH_API_BASE_URL}/GetPatients?id=${searchTerm}`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return [];
      }
      throw error;
    }
  }
);

export const updateBloodBankRequestForm = createAsyncThunk(
  'bloodBank/updateBloodBankRequestForm',
  async ({ id, formData }, { dispatch, rejectWithValue }) => {
    dispatch(setLoading(true));
    try {
      const response = await axios.put(`${API_BASE_URL}BloodBankRequestForms/${id}`, formData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    } finally {
      dispatch(setLoading(false));
    }
  }
);

export const approveBloodBankRequest = createAsyncThunk(
  'bloodBank/approveBloodBankRequest',
  async (id, { dispatch, rejectWithValue }) => {
    dispatch(setLoading(true));
    try {
      const patchData = [
        { "op": "replace", "path": "/status", "value": 1 }
      ];
      
      const response = await axios.patch(
        `${API_BASE_URL}BloodBankRequestForms/${id}`,
        patchData,
        {
          headers: {
            'Content-Type': 'application/json-patch+json'
          }
        }
      );
      
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    } finally {
      dispatch(setLoading(false));
    }
  }
);

// UPDATED: Reject Blood Bank Request with full form payload
export const rejectBloodBankRequest = createAsyncThunk(
  'bloodBank/rejectBloodBankRequest',
  async ({ id, formData }, { dispatch, rejectWithValue }) => {
    dispatch(setLoading(true));
    try {
      const response = await axios.put(
        `${API_BASE_URL}BloodBankRequestForms/ProcessBloodBankRequestForm/${id}`,
        formData,
        {
          headers: {
            'Content-Type': 'application/json-patch+json'
          }
        }
      );
      
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    } finally {
      dispatch(setLoading(false));
    }
  }
);

export const addNewPatient = createAsyncThunk(
  'bloodBank/addNewPatient',
  async (patientData, { dispatch, rejectWithValue }) => {
    dispatch(setLoading(true));
    try {
      const response = await axios.post(`${AUTH_API_BASE_URL}/Login`, {
        Name: patientData.name,
        PhoneNumber: patientData.phoneNumber,
        CountryCode: patientData.countryCode,
        Password: '0000',
        UserType: 'Patient'
      });
      
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.response?.data || error.message);
    } finally {
      dispatch(setLoading(false));
    }
  }
);

export const fetchWholeBloodInventories = createAsyncThunk(
  'bloodBank/fetchWholeBloodInventories',
  async (_, { dispatch, rejectWithValue }) => {
    dispatch(setLoading(true));
    try {
      const response = await axios.get(`${API_BASE_URL}BloodBankInventories/WholeBloodInventory`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    } finally {
      dispatch(setLoading(false));
    }
  }
);

export const performComponentSplit = createAsyncThunk(
  'bloodBank/performComponentSplit',
  async ({ originalUnitId, components }, { dispatch, rejectWithValue }) => {
    dispatch(setLoading(true));
    try {
      const response = await axios.post(
        `${API_BASE_URL}BloodBankInventories/WholeBloodComponentSplit?id=${originalUnitId}`,
        components,
        {
          headers: {
            'Content-Type': 'application/json-patch+json'
          }
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    } finally {
      dispatch(setLoading(false));
    }
  }
);

export const fetchComponentExpiryDate = createAsyncThunk(
  'bloodBank/fetchComponentExpiryDate',
  async ({ componentId, storageDate }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}BloodBankInventories/ComponentExpiryDate?bc=${componentId}&StorageDate=${storageDate}`
      );
      return {
        componentId,
        expiryDate: response.data
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const bloodBankSlice = createSlice({
  name: 'bloodBank',
  initialState: {
    availableBloodUnits: [],
    bloodBankInventories: [],
    bloodBankRequestForms: [],
    pendingRequestForms: [],
    patients: [],
    wholeBloodInventories: [],
    loadingPatients: false,
    error: null,
    successMessage: null,
    addingPatient: false,
    splitLoading: false,
    expiryDates: {},
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    updateRequestFormStatus: (state, action) => {
      const { id, status, isAccepted, rejectionReason } = action.payload;
      const form = state.bloodBankRequestForms.find(form => form.id === id);
      if (form) {
        form.status = status;
        form.isAccepted = isAccepted;
        form.rejectionReason = rejectionReason || '';
      }
      state.pendingRequestForms = state.pendingRequestForms.filter(form => form.id !== id);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addAvailableBloodUnit.fulfilled, (state, action) => {
        state.availableBloodUnits.push(action.payload);
      })
      .addCase(addBloodBankInventory.fulfilled, (state, action) => {
        state.bloodBankInventories.push(action.payload);
      })
      .addCase(addBloodBankRequestForm.fulfilled, (state, action) => {
        state.bloodBankRequestForms.push(action.payload);
        if (action.payload.Status === 0 && action.payload.IsAccepted === false) {
          state.pendingRequestForms.push(action.payload);
        }
      })
      .addCase(updateBloodBankRequestForm.fulfilled, (state, action) => {
        const index = state.bloodBankRequestForms.findIndex(
          form => form.id === action.payload.id
        );
        if (index !== -1) {
          state.bloodBankRequestForms[index] = action.payload;
        }
        if (action.payload.Status !== 0 || action.payload.IsAccepted !== false) {
          state.pendingRequestForms = state.pendingRequestForms.filter(
            form => form.id !== action.payload.id
          );
        }
      })
      .addCase(fetchAvailableBloodUnits.fulfilled, (state, action) => {
        state.availableBloodUnits = action.payload;
      })
      .addCase(fetchBloodBankInventories.fulfilled, (state, action) => {
        state.bloodBankInventories = action.payload;
      })
      .addCase(fetchBloodBankRequestForms.fulfilled, (state, action) => {
        state.bloodBankRequestForms = action.payload;
      })
      .addCase(fetchPendingBloodBankRequestForms.fulfilled, (state, action) => {
        state.pendingRequestForms = action.payload;
      })
      .addCase(fetchWholeBloodInventories.fulfilled, (state, action) => {
        state.wholeBloodInventories = action.payload;
      })
      .addCase(fetchPatientsBySearch.pending, (state) => {
        state.loadingPatients = true;
        state.error = null;
      })
      .addCase(fetchPatientsBySearch.fulfilled, (state, action) => {
        state.loadingPatients = false;
        state.patients = action.payload;
      })
      .addCase(fetchPatientsBySearch.rejected, (state, action) => {
        state.loadingPatients = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(approveBloodBankRequest.fulfilled, (state, action) => {
        state.pendingRequestForms = state.pendingRequestForms.filter(
          form => form.Id !== action.payload.Id
        );
        state.successMessage = `Request #${action.payload.Id} successfully approved!`;
        state.error = null;
      })
      .addCase(rejectBloodBankRequest.fulfilled, (state, action) => {
        state.pendingRequestForms = state.pendingRequestForms.filter(
          form => form.Id !== action.payload.Id
        );
        state.successMessage = `Request #${action.payload.Id} has been rejected.`;
        state.error = null;
      })
      .addCase(addNewPatient.pending, (state) => {
        state.addingPatient = true;
        state.error = null;
      })
      .addCase(addNewPatient.fulfilled, (state, action) => {
        state.addingPatient = false;
        state.successMessage = 'Patient added successfully!';
        state.error = null;
        state.patients.push(action.payload);
      })
      .addCase(addNewPatient.rejected, (state, action) => {
        state.addingPatient = false;
        state.error = action.payload || 'Failed to add patient';
        state.successMessage = null;
      })
      .addCase(performComponentSplit.pending, (state) => {
        state.splitLoading = true;
        state.error = null;
      })
      .addCase(performComponentSplit.fulfilled, (state, action) => {
        state.splitLoading = false;
        state.successMessage = 'Blood unit successfully split into components!';
        state.error = null;
      })
      .addCase(performComponentSplit.rejected, (state, action) => {
        state.splitLoading = false;
        state.error = action.payload || 'Failed to split blood unit';
        state.successMessage = null;
      })
      .addCase(fetchComponentExpiryDate.fulfilled, (state, action) => {
        const { componentId, expiryDate } = action.payload;
        state.expiryDates[componentId] = expiryDate;
      })
      .addMatcher(
        (action) => action.type.endsWith('/rejected'),
        (state, action) => {
          state.successMessage = null;
          if (action.type !== 'bloodBank/fetchPatientsBySearch/rejected') {
            state.error = action.payload || action.error.message;
          }
        }
      )
  },
});

export const { clearError, clearSuccessMessage, updateRequestFormStatus } = bloodBankSlice.actions;
export default bloodBankSlice.reducer;